/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getOrdersPlanningsListSelector,
	getIsOrdersPlanningsListLoadingSelector,
	getPaginationSelector,
	getGroupsSelector,
	getIsGroupsLoadingSelector,
	getIsConfigsLoadingSelector,
	getConfigsSelector,
} from './selectors';
import { getConfigsAction, getGroupsAction, getOrdersPlanningsListAction, resetStateAction } from './actions';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { TABLE_ORDERS_PLANNINGS_COLUMNS } from './constants';
import { getMenuByRules } from '../orders/utils';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';

const OrderPlanningsContainerWrapper = ({
	onGetOrdersPlanningsList,
	ordersPlanningsList,
	isOrdersPlanningsListLoading,
	pagination,
	onResetState,
	onGetGroups,
	groups,
	isGroupsLoading,
	onGetConfigs,
	configs,
	isConfigsLoading,
}) => {
	useInjectSaga({ key: 'ordersPlanningsContainerSaga', saga });
	useInjectReducer({ key: 'ordersPlanningsContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetOrdersPlanningsList({ searchQuery: removeModalQuery(query.toString()) });
		onGetGroups();
		onGetConfigs();

		return () => onResetState();
	}, []);

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetOrdersPlanningsList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetOrdersPlanningsList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetOrdersPlanningsList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(
				normalizeDataGridModel(filterModel, TABLE_ORDERS_PLANNINGS_COLUMNS(groups, configs)),
			);

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetOrdersPlanningsList({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={getMenuByRules()}
			isInnerRouter
			pageContent={
				<DataGridComponent
					headerText="История планирования"
					columns={TABLE_ORDERS_PLANNINGS_COLUMNS(groups, configs)}
					rows={ordersPlanningsList}
					pagination={
						!pagination
							? null
							: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
					}
					isTableDataLoading={isOrdersPlanningsListLoading || isGroupsLoading || isConfigsLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	ordersPlanningsList: getOrdersPlanningsListSelector(),
	isOrdersPlanningsListLoading: getIsOrdersPlanningsListLoadingSelector(),
	pagination: getPaginationSelector(),
	groups: getGroupsSelector(),
	isGroupsLoading: getIsGroupsLoadingSelector(),
	configs: getConfigsSelector(),
	isConfigsLoading: getIsConfigsLoadingSelector(),
});

const mapDispatchToProps = {
	onGetOrdersPlanningsList: getOrdersPlanningsListAction,
	onGetGroups: getGroupsAction,
	onGetConfigs: getConfigsAction,
	onResetState: resetStateAction,
};

const OrderPlanningsContainer = connect(mapStateToProps, mapDispatchToProps)(OrderPlanningsContainerWrapper);

OrderPlanningsContainerWrapper.propTypes = {
	onGetOrdersPlanningsList: PropTypes.func.isRequired,
	ordersPlanningsList: PropTypes.array.isRequired,
	isOrdersPlanningsListLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onGetGroups: PropTypes.func.isRequired,
	groups: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	isGroupsLoading: PropTypes.bool.isRequired,
	onGetConfigs: PropTypes.func.isRequired,
	configs: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	isConfigsLoading: PropTypes.bool.isRequired,
	onResetState: PropTypes.func,
};

export default OrderPlanningsContainer;
